/**
 * Family Chat (§22.14) — parent side, iMessage-style.
 *
 * Goals: flat, calm, lots of whitespace. No card chrome. Two solid bubble
 * colors. A single hairline divider per region.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Home,
  PhoneCall,
  Send,
  MessageCircle,
  Phone,
  Info,
  Check,
  CheckCheck,
  ChevronLeft,
} from 'lucide-react';

import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { chatApi, type ChatMessage, type Conversation } from '@/api/chat';
import { usersApi, type Child } from '@/api/users';
import { ChildAvatar } from '@/components/ChildAvatar';
import { PageHeader } from './PageHeader';
import { EmptyState } from './EmptyState';

// ── Time helpers ────────────────────────────────────────────────────────────

function timeLabel(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

function relativeShort(iso: string | null): string {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return 'now';
  const mins = Math.floor(ms / 60_000);
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function dateHeading(iso: string): string {
  const d = new Date(iso);
  const today = new Date();
  const yest = new Date();
  yest.setDate(yest.getDate() - 1);
  const isSameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  if (isSameDay(d, today)) return `Today · ${timeLabel(iso)}`;
  if (isSameDay(d, yest)) return `Yesterday · ${timeLabel(iso)}`;
  return d.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' });
}

function previewBody(m: ChatMessage | null): string {
  if (!m) return 'No messages yet';
  if (m.kind === 'quick_action') {
    const a = (m.payload as { action?: string } | null)?.action ?? '';
    if (a === 'come_home') return '🏠 Come home';
    if (a === 'call_me') return '📞 Call me';
    if (a === 'im_okay') return '✅ I’m okay';
    return m.body ?? 'Quick action';
  }
  if (m.kind === 'location') return '📍 Location';
  if (m.kind === 'voice') return '🎤 Voice note';
  return m.body ?? '';
}

// ── Page ────────────────────────────────────────────────────────────────────

export function ChatPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const children = useQuery({ queryKey: ['children'], queryFn: () => usersApi.listChildren(), retry: false });
  const conversations = useQuery({
    queryKey: ['chat', 'conversations'],
    queryFn: () => chatApi.listConversations(),
    refetchInterval: 10_000,
    retry: false,
  });

  const [selectedId, setSelectedId] = useState<string | null>(null);
  useEffect(() => {
    if (!selectedId && (children.data?.length ?? 0) > 0) {
      setSelectedId(children.data![0]!.id);
    }
  }, [children.data, selectedId]);

  const childById = useMemo(
    () => new Map((children.data ?? []).map((c) => [c.id, c])),
    [children.data],
  );
  const convByChild = useMemo(() => {
    const m = new Map<string, Conversation>();
    for (const c of conversations.data ?? []) m.set(c.child_id, c);
    return m;
  }, [conversations.data]);

  return (
    <div className="px-6 py-8 md:px-10 md:py-10">
      <PageHeader
        title={t('pages.chat.title')}
        icon={MessageCircle}
        tint="coral"
        description={t('pages.chat.description')}
      />

      {/* iMessage-style shell — desktop is the classic two-column layout
          (sidebar + thread); mobile collapses to a single pane that toggles
          between the child list and the open conversation, with a Back chevron
          to return. Without this, a 288 px sidebar leaves only ~87 px for the
          thread on a 375 px phone. */}
      <div className="mt-6 grid h-[72vh] grid-cols-1 overflow-hidden rounded-2xl bg-white ring-1 ring-[color:var(--kz-color-border-subtle)]/60 md:grid-cols-[18rem_1fr]">
        {/* Sidebar */}
        <aside
          className={cn(
            'flex flex-col overflow-y-auto border-[color:var(--kz-color-border-subtle)]/60 md:border-r',
            selectedId ? 'hidden md:flex' : 'flex',
          )}
        >
          <div className="px-4 py-3">
            <p className="text-xs font-semibold uppercase tracking-wider text-[color:var(--kz-color-ink-muted)]">
              Children
            </p>
          </div>
          {(children.data ?? []).map((c) => {
            const conv = convByChild.get(c.id);
            return (
              <ChildRow
                key={c.id}
                child={c}
                conversation={conv}
                selected={selectedId === c.id}
                onSelect={() => setSelectedId(c.id)}
              />
            );
          })}
          {(children.data?.length ?? 0) === 0 && (
            <p className="mx-4 my-3 text-xs text-[color:var(--kz-color-ink-muted)]">
              Add a child first to start a conversation.
            </p>
          )}
        </aside>

        {/* Thread */}
        {selectedId ? (
          <div className="flex min-h-0 flex-col">
            <button
              type="button"
              onClick={() => setSelectedId(null)}
              className="flex items-center gap-1 border-b border-[color:var(--kz-color-border-subtle)]/60 px-3 py-2 text-sm font-medium text-[color:var(--kz-color-ink-primary)] hover:bg-[color:var(--kz-color-surface-sunken)]/60 md:hidden"
            >
              <ChevronLeft className="size-4" />
              Children
            </button>
            <ChatThread
              child={childById.get(selectedId) ?? null}
              conversation={convByChild.get(selectedId) ?? null}
              childId={selectedId}
              onActivity={() => {
                void qc.invalidateQueries({ queryKey: ['chat', 'conversations'] });
              }}
            />
          </div>
        ) : (
          <div className="hidden place-items-center text-sm text-[color:var(--kz-color-ink-muted)] md:grid">
            Pick a child to open the conversation.
          </div>
        )}
      </div>
    </div>
  );
}

// ── Child row in the sidebar ────────────────────────────────────────────────

function ChildRow({
  child,
  conversation,
  selected,
  onSelect,
}: {
  child: Child;
  conversation: Conversation | undefined;
  selected: boolean;
  onSelect: () => void;
}) {
  const unread = conversation?.unread_count ?? 0;
  const last = conversation?.last_message ?? null;
  const lastAt = conversation?.last_message_at ?? null;
  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        'flex w-full items-start gap-3 px-4 py-3 text-left transition-colors',
        selected
          ? 'bg-[#4f6bff]/8'
          : 'hover:bg-[color:var(--kz-color-surface-sunken)]/60',
      )}
    >
      <ChildAvatar
        name={child.name}
        color={child.color}
        avatarUrl={child.avatar_url}
        size="size-12"
      />
      <span className="min-w-0 flex-1">
        <span className="flex items-baseline justify-between gap-2">
          <span className="truncate text-[15px] font-semibold text-[color:var(--kz-color-ink-primary)]">
            {child.name}
          </span>
          {lastAt && (
            <span className="shrink-0 text-[11px] tabular-nums text-[color:var(--kz-color-ink-muted)]">
              {relativeShort(lastAt)}
            </span>
          )}
        </span>
        <span className="mt-0.5 flex items-center gap-2">
          <span
            className={cn(
              'block flex-1 truncate text-[13px]',
              unread > 0 ? 'font-medium text-[color:var(--kz-color-ink-primary)]' : 'text-[color:var(--kz-color-ink-muted)]',
            )}
          >
            {last
              ? `${last.sender_type === 'parent' ? 'You: ' : ''}${previewBody(last)}`
              : 'Say hi 👋'}
          </span>
          {unread > 0 && (
            <span className="grid size-5 shrink-0 place-items-center rounded-full bg-[#4f6bff] text-[10px] font-bold text-white">
              {unread > 9 ? '9+' : unread}
            </span>
          )}
        </span>
      </span>
    </button>
  );
}

// ── Thread ──────────────────────────────────────────────────────────────────

function ChatThread({
  child,
  conversation,
  childId,
  onActivity,
}: {
  child: Child | null;
  conversation: Conversation | null;
  childId: string;
  onActivity: () => void;
}) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState('');
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const messages = useQuery({
    queryKey: ['chat', 'messages', childId],
    queryFn: () => chatApi.getMessages(childId),
    refetchInterval: 8_000,
    retry: false,
  });

  // Mark the thread read whenever it's opened / refetched with unread items.
  useEffect(() => {
    if (!messages.data) return;
    const hasUnreadFromChild = messages.data.some(
      (m) => m.sender_type === 'child' && !m.read_at,
    );
    if (hasUnreadFromChild) {
      void chatApi.markRead(childId).then(() => {
        void qc.invalidateQueries({ queryKey: ['chat', 'messages', childId] });
        onActivity();
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages.data, childId]);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.data]);

  const send = useMutation({
    mutationFn: (input: Parameters<typeof chatApi.send>[1]) => chatApi.send(childId, input),
    onSuccess: () => {
      setDraft('');
      void qc.invalidateQueries({ queryKey: ['chat', 'messages', childId] });
      onActivity();
    },
    onError: () => toast.error('Could not send.'),
  });

  // Whether message[i] is the *last* in a same-sender streak (used to decide
  // whether to round the bottom corner like an iMessage tail).
  const isStreakEndAt = useMemo(() => {
    const arr = messages.data ?? [];
    const out: boolean[] = new Array(arr.length).fill(false);
    for (let i = 0; i < arr.length; i++) {
      const next = arr[i + 1];
      out[i] = !next || next.sender_type !== arr[i]!.sender_type;
    }
    return out;
  }, [messages.data]);

  const canSend = draft.trim().length > 0 && !send.isPending;

  return (
    <div className="flex h-full flex-col">
      {/* Header — centered avatar + name, like iMessage. */}
      <div className="flex items-center justify-between border-b border-[color:var(--kz-color-border-subtle)]/60 px-4 py-3">
        <div className="size-9" /> {/* spacer for symmetry */}
        <div className="flex flex-col items-center gap-1">
          {child && (
            <ChildAvatar
              name={child.name}
              color={child.color}
              avatarUrl={child.avatar_url}
              size="size-8"
            />
          )}
          <p className="text-[13px] font-semibold text-[color:var(--kz-color-ink-primary)]">
            {child?.name ?? 'Conversation'}
          </p>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            aria-label="Call"
            className="grid size-9 place-items-center rounded-full text-[#4f6bff] transition-colors hover:bg-[#4f6bff]/8"
          >
            <Phone className="size-[18px]" />
          </button>
          <button
            type="button"
            aria-label="Conversation info"
            className="grid size-9 place-items-center rounded-full text-[color:var(--kz-color-ink-muted)] transition-colors hover:bg-[color:var(--kz-color-surface-sunken)]"
          >
            <Info className="size-[18px]" />
          </button>
        </div>
      </div>

      {/* Pinned banner (when set) — single hairline strip. */}
      {conversation?.pinned_text && (
        <div className="border-b border-[color:var(--kz-color-border-subtle)]/60 px-4 py-2 text-[12px]">
          <span className="font-semibold text-[#4f6bff]">Pinned · </span>
          <span className="text-[color:var(--kz-color-ink-secondary)]">{conversation.pinned_text}</span>
        </div>
      )}

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4">
        {messages.isPending && (
          <div className="space-y-2">
            <Skeleton className="ml-0 h-8 w-2/3 rounded-2xl" />
            <Skeleton className="ml-auto h-8 w-1/2 rounded-2xl" />
            <Skeleton className="ml-0 h-8 w-3/4 rounded-2xl" />
          </div>
        )}
        {messages.data?.length === 0 && (
          <EmptyState
            icon={MessageCircle}
            tint="coral"
            title="No messages yet"
            description="Say hi 👋 to start the conversation."
          />
        )}
        <div className="flex flex-col gap-0.5">
          {messages.data?.map((m, i) => {
            const prev = messages.data?.[i - 1];
            const newDay =
              !prev ||
              new Date(prev.created_at).toDateString() !== new Date(m.created_at).toDateString();
            const sameAsPrev = prev && prev.sender_type === m.sender_type;
            return (
              <div key={m.id}>
                {newDay && (
                  <p className="my-3 text-center text-[11px] font-medium text-[color:var(--kz-color-ink-muted)]">
                    {dateHeading(m.created_at)}
                  </p>
                )}
                <Bubble
                  message={m}
                  streakEnd={isStreakEndAt[i] ?? false}
                  streakStart={!sameAsPrev}
                />
              </div>
            );
          })}
        </div>
      </div>

      {/* Composer */}
      <div className="border-t border-[color:var(--kz-color-border-subtle)]/60 px-4 py-3">
        <div className="mb-2 flex flex-wrap gap-2">
          <QuickAction
            icon={Home}
            label="Come home"
            onClick={() => send.mutate({ kind: 'quick_action', action: 'come_home' })}
            disabled={send.isPending}
          />
          <QuickAction
            icon={PhoneCall}
            label="Call me"
            onClick={() => send.mutate({ kind: 'quick_action', action: 'call_me' })}
            disabled={send.isPending}
          />
        </div>
        <form
          className="flex items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            const body = draft.trim();
            if (body) send.mutate({ kind: 'text', body });
          }}
        >
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="iMessage"
            maxLength={2000}
            className="h-10 flex-1 rounded-full bg-[color:var(--kz-color-surface-sunken)] px-4 text-[14px] text-[color:var(--kz-color-ink-primary)] placeholder:text-[color:var(--kz-color-ink-muted)] focus:bg-white focus:outline-none focus:ring-1 focus:ring-[#4f6bff]/40"
          />
          <button
            type="submit"
            disabled={!canSend}
            aria-label="Send"
            className={cn(
              'grid size-10 shrink-0 place-items-center rounded-full transition-all',
              canSend
                ? 'bg-[#4f6bff] text-white shadow-sm hover:bg-[#3a55ee]'
                : 'cursor-not-allowed bg-[color:var(--kz-color-surface-sunken)] text-[color:var(--kz-color-ink-muted)]',
            )}
          >
            <Send className="size-[18px]" />
          </button>
        </form>
      </div>
    </div>
  );
}

// ── Quick-action chip ──────────────────────────────────────────────────────

function QuickAction({
  icon: Icon,
  label,
  onClick,
  disabled,
}: {
  icon: typeof Home;
  label: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="inline-flex items-center gap-1.5 rounded-full bg-[color:var(--kz-color-surface-sunken)] px-3 py-1.5 text-[12px] font-semibold text-[#4f6bff] transition-colors hover:bg-[#4f6bff]/10 disabled:opacity-50"
    >
      <Icon className="size-3.5" />
      {label}
    </button>
  );
}

// ── Bubble ──────────────────────────────────────────────────────────────────

function Bubble({
  message: m,
  streakEnd,
  streakStart,
}: {
  message: ChatMessage;
  streakEnd: boolean;
  streakStart: boolean;
}) {
  // System = automated safety check-in. Centered small text, no bubble.
  if (m.sender_type === 'system') {
    return (
      <p className="my-1 px-4 text-center text-[11px] italic text-[color:var(--kz-color-ink-muted)]">
        {m.body} · {timeLabel(m.created_at)}
      </p>
    );
  }

  const mine = m.sender_type === 'parent';
  const wasRead = mine && m.read_at !== null;

  return (
    <div className={cn('flex flex-col', mine ? 'items-end' : 'items-start')}>
      <div
        className={cn(
          'max-w-[70%] px-3.5 py-2 text-[15px] leading-snug',
          // iMessage-style: every bubble is rounded-3xl by default, and we
          // only "snip" the tail corner on the last of a streak.
          'rounded-3xl',
          mine
            ? cn(
                'bg-[#4f6bff] text-white',
                streakEnd && 'rounded-br-md',
                !streakStart && 'rounded-tr-md',
              )
            : cn(
                'bg-[color:var(--kz-color-surface-sunken)] text-[color:var(--kz-color-ink-primary)]',
                streakEnd && 'rounded-bl-md',
                !streakStart && 'rounded-tl-md',
              ),
        )}
      >
        <p className="whitespace-pre-wrap break-words">{m.body}</p>
      </div>
      {streakEnd && (
        <p
          className={cn(
            'mt-0.5 flex items-center gap-1 px-1 text-[10px]',
            'text-[color:var(--kz-color-ink-muted)]',
          )}
        >
          <span className="tabular-nums">{timeLabel(m.created_at)}</span>
          {mine && (
            <span aria-label={wasRead ? 'Read' : 'Sent'} title={wasRead ? 'Read' : 'Sent'}>
              {wasRead ? <CheckCheck className="size-3" /> : <Check className="size-3" />}
            </span>
          )}
        </p>
      )}
    </div>
  );
}
